import * as Types from './metricsTypes';

const METRICS_CACHE_STORAGE_KEY = 'metricsCache';
const METRICS_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// Load cache from localStorage on initialization
const loadCacheFromStorage = () => {
  try {
    const stored = localStorage.getItem(METRICS_CACHE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const now = Date.now();
      const filteredCache = {};
      Object.keys(parsed).forEach(key => {
        const entry = parsed[key];
        // Keep cache if less than 1 hour old AND has actual data (reject empty {} from silent failures)
        const hasValidData = entry.data &&
          typeof entry.data === 'object' &&
          !Array.isArray(entry.data) &&
          Object.keys(entry.data).length > 0;
        if (entry.timestamp && (now - entry.timestamp) < METRICS_CACHE_TTL_MS && hasValidData) {
          filteredCache[key] = entry;
        }
      });
      return filteredCache;
    }
  } catch (error) {
    console.error('Error loading metrics cache from localStorage:', error);
  }
  return {};
};

// Save cache to localStorage
const saveCacheToStorage = (cache) => {
  try {
    localStorage.setItem(METRICS_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Error saving metrics cache to localStorage:', error);
  }
};

const initialState = {
  cache: loadCacheFromStorage(), // Load from localStorage
  loading: false,
  error: null
};

const metricsReducer = (state = initialState, action) => {
  switch (action.type) {
    case Types.FETCH_METRICS_REQUEST: {
      const { cacheKey } = action.payload;
      return {
        ...state,
        cache: {
          ...state.cache,
          [cacheKey]: {
            ...state.cache[cacheKey],
            loading: true,
            error: null
          }
        }
      };
    }

    case Types.FETCH_METRICS_SUCCESS: {
      const { cacheKey, data, timestamp } = action.payload;
      const newCache = {
        ...state.cache,
        [cacheKey]: {
          data,
          timestamp,
          loading: false,
          error: null
        }
      };
      // Save to localStorage
      saveCacheToStorage(newCache);
      return {
        ...state,
        cache: newCache
      };
    }

    case Types.FETCH_METRICS_FAILURE: {
      const { cacheKey, error } = action.payload;
      return {
        ...state,
        cache: {
          ...state.cache,
          [cacheKey]: {
            ...state.cache[cacheKey],
            loading: false,
            error
          }
        }
      };
    }

    case Types.FETCH_ALL_METRICS_REQUEST: {
      const { cacheKey } = action.payload;
      return {
        ...state,
        cache: {
          ...state.cache,
          [cacheKey]: {
            ...state.cache[cacheKey],
            loading: true,
            error: null
          }
        }
      };
    }

    case Types.FETCH_ALL_METRICS_SUCCESS: {
      const { cacheKey, data, timestamp } = action.payload;
      const newCache = {
        ...state.cache,
        [cacheKey]: {
          data,
          timestamp,
          loading: false,
          error: null
        }
      };
      saveCacheToStorage(newCache);
      return {
        ...state,
        cache: newCache
      };
    }

    case Types.FETCH_ALL_METRICS_FAILURE: {
      const { cacheKey, error } = action.payload;
      return {
        ...state,
        cache: {
          ...state.cache,
          [cacheKey]: {
            ...state.cache[cacheKey],
            loading: false,
            error
          }
        }
      };
    }

    case Types.CLEAR_METRICS_CACHE: {
      // If specific cacheKey provided, clear only that key
      if (action.payload?.cacheKey) {
        const { [action.payload.cacheKey]: removed, ...remainingCache } = state.cache;
        saveCacheToStorage(remainingCache);
        return {
          ...state,
          cache: remainingCache
        };
      }
      // Otherwise clear all cache
      localStorage.removeItem(METRICS_CACHE_STORAGE_KEY);
      return {
        ...state,
        cache: {}
      };
    }

    default:
      return state;
  }
};

export default metricsReducer;
